// @owners { team: patients-team }
import { useMemo } from 'react';
import { getNextDeliveryForPrescriptionID } from '~shared/features/delivery/selectors/getNextDeliveryForPrescriptionID';
import { getUpcomingDeliveriesByPrescriptionID } from '~shared/features/delivery/selectors/getUpcomingDeliveriesForPrescription';
import { useNextAvailableDatesByPrescriptionIdFromQueryCache } from '~shared/features/next-available-date/queries/useNextAvailableDatesByPrescriptionIdFromQueryCache';
import { getNextAvailableDateFacilityID } from '~shared/features/next-available-date/selectors/getNextAvailableDateFacilityId';
import { getPrescriptionByID } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import { isPrescriptionScheduleable } from '~shared/features/prescriptions/selectors/getScheduleableForPrescriptionID';
import { type PrescriptionIDParam } from '~shared/selectors/selectorParamTypes';
import { useSelectorShared } from '~shared/store';

export type UseIsPrescriptionScheduleableParams = PrescriptionIDParam & {
  nextAvailableDateAddressID?: number;
};

/**
 * Whether the given prescription is scheduleable, using **strategy A** NAD payloads from the checkout React Query
 * cache ({@link useNextAvailableDatesByPrescriptionIdFromQueryCache}) when address or facility context exists —
 * matching {@link getNextAvailableDatesByPrescriptionID} when passed a `queryClient`.
 */
export const useIsPrescriptionScheduleable = ({
  prescriptionID,
  nextAvailableDateAddressID,
}: UseIsPrescriptionScheduleableParams): boolean => {
  const prescription = useSelectorShared((state) => getPrescriptionByID(state, prescriptionID));
  const delivery = useSelectorShared((state) => getNextDeliveryForPrescriptionID(state, { prescriptionID }));
  const upcomingDeliveriesByPrescriptionID = useSelectorShared(getUpcomingDeliveriesByPrescriptionID);
  const facilityID = useSelectorShared(getNextAvailableDateFacilityID);
  const nextAvailableDatesByPrescriptionID = useNextAvailableDatesByPrescriptionIdFromQueryCache();

  return useMemo(() => {
    if (!prescription) {
      return false;
    }
    const hasNadContext = Boolean(nextAvailableDateAddressID || facilityID);
    const nextAvailableDate = hasNadContext ? nextAvailableDatesByPrescriptionID[prescriptionID] : undefined;
    const upcomingDeliveries = upcomingDeliveriesByPrescriptionID[prescriptionID] ?? [];

    return isPrescriptionScheduleable(prescription, delivery, upcomingDeliveries, nextAvailableDate);
  }, [
    delivery,
    facilityID,
    nextAvailableDateAddressID,
    nextAvailableDatesByPrescriptionID,
    prescription,
    prescriptionID,
    upcomingDeliveriesByPrescriptionID,
  ]);
};
